import { useState, useEffect, useCallback, useRef } from 'react';
import { docuwareInvoicesApi } from '../services/docuwareInvoicesApi';
import type {
    DocuWareInvoiceRow, DocuWareCompanySummary, DocuWareFacets,
    DocuWareInvoiceFilters, DocuWareSortField, DocuWarePostingStatus,
} from '../types/docuwareInvoices';

/**
 * State for /docuware-invoices: filters, sort, page, data.
 *
 * Phase 1 kept all of this inline in the page and said so in a comment — one search box, one
 * company filter, one fixed sort did not earn a hook. Eleven filters and thirteen sortable
 * columns do.
 *
 * Sorting and filtering are SERVER-side (the endpoint is paginated, so anything else would
 * sort one page instead of the result set), which is why every setter resets the page: the
 * row that was on page 3 is somewhere else entirely once the order changes.
 */

export const PAGE_SIZE = 50;
const STORAGE_KEY = 'docuware-invoices.filters.v1';

const SORT_FIELDS: DocuWareSortField[] = [
    'company', 'stored', 'received', 'supplier', 'invoiceNumber', 'invoiceDate',
    'amount', 'currency', 'documentType', 'invoiceCategory', 'workflowStatus',
    'bcDocumentNo', 'bcPostingDate',
];

/** Dates and numbers read better newest/largest first; text reads better A-Z. */
const DESC_BY_DEFAULT = new Set<DocuWareSortField>([
    'stored', 'received', 'invoiceDate', 'amount', 'bcPostingDate',
]);

const EMPTY_FACETS: DocuWareFacets = {
    documentType: [], invoiceCategory: [], workflowStatus: [], currency: [],
};

const FILTER_KEYS: (keyof DocuWareInvoiceFilters)[] = [
    'companyCode', 'search', 'postingStatus', 'storedFrom', 'storedTo',
    'invoiceDateFrom', 'invoiceDateTo', 'documentType', 'invoiceCategory',
    'workflowStatus', 'currency', 'amountMin', 'amountMax',
];

/**
 * Restored state is untrusted input: it was written by an older build, and a stale key can
 * outlive the shape it was written for. Anything unrecognised is dropped rather than passed
 * on to the API.
 */
function sanitizeFilters(raw: unknown): DocuWareInvoiceFilters {
    if (!raw || typeof raw !== 'object') return {};
    const source = raw as Record<string, unknown>;
    const out: DocuWareInvoiceFilters = {};
    for (const key of FILTER_KEYS) {
        const value = source[key];
        if (typeof value === 'string' && value !== '') {
            (out as Record<string, string>)[key] = value;
        }
    }
    if (out.postingStatus && !['all', 'open', 'posted'].includes(out.postingStatus)) {
        delete out.postingStatus;
    }
    return out;
}

function loadStoredFilters(): DocuWareInvoiceFilters {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? sanitizeFilters(JSON.parse(raw)) : {};
    } catch {
        return {};
    }
}

export function useDocuWareInvoices(enabled: boolean) {
    const [rows, setRows] = useState<DocuWareInvoiceRow[]>([]);
    const [companies, setCompanies] = useState<DocuWareCompanySummary[]>([]);
    const [facets, setFacets] = useState<DocuWareFacets>(EMPTY_FACETS);
    const [lastImport, setLastImport] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFiltersState] = useState<DocuWareInvoiceFilters>(loadStoredFilters);
    const [sortBy, setSortBy] = useState<DocuWareSortField>('stored');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);

    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
        } catch {
            // A full or blocked sessionStorage must not take the page down with it.
        }
    }, [filters]);

    const setFilters = useCallback((next: DocuWareInvoiceFilters) => {
        setFiltersState(next);
        setPage(1);
    }, []);

    const setSort = useCallback((field: DocuWareSortField) => {
        if (!SORT_FIELDS.includes(field)) return;
        setSortBy((prev) => {
            if (prev === field) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
            else setSortOrder(DESC_BY_DEFAULT.has(field) ? 'desc' : 'asc');
            return field;
        });
        setPage(1);
    }, []);

    // Out-of-order responses: filters change fast, and a slow earlier request must not
    // overwrite the result of a later one with stale rows.
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await docuwareInvoicesApi.list({
                page, limit: PAGE_SIZE, filters, sortBy, sortOrder,
            });
            if (seq !== requestSeq.current) return;
            setRows(result.items);
            setCompanies(result.companies);
            setFacets(result.facets);
            setLastImport(result.lastImport);
            setTotal(result.total);
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load invoices');
            setRows([]);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [page, filters, sortBy, sortOrder]);

    useEffect(() => { if (enabled) void load(); }, [enabled, load]);

    const activeFilterCount = FILTER_KEYS.filter((k) => {
        const v = filters[k];
        return v !== undefined && v !== '' && !(k === 'postingStatus' && v === 'all');
    }).length;

    return {
        rows, companies, facets, lastImport, total, loading, error,
        filters, setFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
        reload: load,
    };
}

export type { DocuWarePostingStatus };
